import { useId, useMemo, useState } from 'react';
import { DATE_LABEL_MAX, formatCalendarDate, fromAbsoluteDay, parseSortKey, toAbsoluteDay, type CalendarSystem, type ChronologyDate } from '@realmery/realm-core';

/**
 * Form parts the Chronology and Story Studio share (docs/PARITY-STEP-3-PLAN.md section 4,
 * PARITY-STEP-5-PLAN.md section 4): a date as a sort key, a label and Uncertain, with Pick a
 * date in the active calendar; and checkboxes for choosing many things.
 */

export interface DateDraft {
  readonly key: string;
  readonly label: string;
  readonly uncertain: boolean;
}

export const draftOf = (date: ChronologyDate | null | undefined): DateDraft => ({ key: date ? date.sortKey.toString() : '0', label: date?.label ?? '', uncertain: date?.uncertain ?? false });


/** A sort key, a label and Uncertain, and with an active calendar, Pick a date (decision 1). */
export function DateFields({ legend, value, onChange, calendar }: { legend: string; value: DateDraft; onChange: (d: DateDraft) => void; calendar: CalendarSystem | null }) {
  const id = useId();
  const [picking, setPicking] = useState(false);
  return (
    <fieldset className="date-fields">
      <legend>{legend}</legend>
      <div className="date-row">
        <label>
          Sort key
          <input value={value.key} inputMode="numeric" required onChange={(e) => onChange({ ...value, key: e.currentTarget.value })} />
        </label>
        <label className="grow">
          Label
          <input value={value.label} maxLength={DATE_LABEL_MAX} placeholder="e.g. Year 420, 450 BCE" onChange={(e) => onChange({ ...value, label: e.currentTarget.value })} />
        </label>
        <label className="check">
          <input type="checkbox" checked={value.uncertain} onChange={(e) => onChange({ ...value, uncertain: e.currentTarget.checked })} />
          Uncertain
        </label>
      </div>
      {calendar && !picking && (
        <button type="button" className="quiet" aria-describedby={`${id}-cal`} onClick={() => setPicking(true)}>
          Pick a date…
        </button>
      )}
      <span id={`${id}-cal`} className="visually-hidden">
        in {calendar?.name}
      </span>
      {calendar && picking && <DatePicker calendar={calendar} value={value} onPick={(d) => (onChange(d), setPicking(false))} onCancel={() => setPicking(false)} />}
    </fieldset>
  );
}

export function DatePicker({ calendar, value, onPick, onCancel }: { calendar: CalendarSystem; value: DateDraft; onPick: (d: DateDraft) => void; onCancel: () => void }) {
  const current = useMemo(() => {
    try {
      return fromAbsoluteDay(calendar, Number(parseSortKey(value.key)));
    } catch {
      return fromAbsoluteDay(calendar, 0);
    }
  }, [calendar, value.key]);
  const [year, setYear] = useState(String(current.year));
  const [month, setMonth] = useState(current.monthIndex);
  const [day, setDay] = useState(String(current.day));
  const length = calendar.months[month]?.lengthInDays ?? 1;
  const yearNumber = Number(year);
  const dayNumber = Number(day);
  const valid = Number.isInteger(yearNumber) && Math.abs(yearNumber) <= 2_000_000_000 && Number.isInteger(dayNumber) && dayNumber >= 1 && dayNumber <= length;
  const absolute = valid ? toAbsoluteDay(calendar, yearNumber, month, dayNumber) : null;
  const safe = absolute !== null && Number.isSafeInteger(absolute);
  return (
    <div className="date-picker" role="group" aria-label={`Pick a date in ${calendar.name}`}>
      <label>
        Year
        <input value={year} inputMode="numeric" onChange={(e) => setYear(e.currentTarget.value)} />
      </label>
      <label>
        Month
        <select value={month} onChange={(e) => setMonth(Number(e.currentTarget.value))}>
          {calendar.months.map((m, i) => (
            <option key={i} value={i}>
              {m.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Day
        <input value={day} inputMode="numeric" onChange={(e) => setDay(e.currentTarget.value)} />
      </label>
      <p className="meta" aria-live="polite">
        {!valid ? `Choose a year and a day from 1 to ${length}.` : !safe ? 'That date is too far out to use.' : `${formatCalendarDate(calendar, absolute!)} · sort key ${absolute}`}
      </p>
      <div className="actions">
        <button type="button" disabled={!safe} onClick={() => onPick({ ...value, key: String(absolute), label: formatCalendarDate(calendar, absolute!) })}>
          Use this date
        </button>
        <button type="button" className="quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Checkboxes for many things, with a search when there are more than a few. */
export function Picker({
  legend,
  items,
  chosen,
  onChange,
  searchLabel,
  none,
}: {
  legend: string;
  items: readonly { id: string; name: string; note: string }[];
  chosen: readonly string[];
  onChange: (ids: readonly string[]) => void;
  searchLabel: string;
  none: string;
}) {
  const [query, setQuery] = useState('');
  const chosenSet = new Set(chosen);
  const q = query.trim().toLowerCase();
  const matches = items.filter((item) => chosenSet.has(item.id) || q === '' || item.name.toLowerCase().includes(q));
  // Chosen first, so they're never scrolled out of sight.
  const shown = [...matches.filter((i) => chosenSet.has(i.id)), ...matches.filter((i) => !chosenSet.has(i.id))].slice(0, Math.max(60, chosen.length));
  const toggle = (id: string, on: boolean) => onChange(on ? [...chosen, id] : chosen.filter((c) => c !== id));
  return (
    <fieldset className="picker">
      <legend>
        {legend} {chosen.length > 0 && <span className="meta">({chosen.length} chosen)</span>}
      </legend>
      {items.length === 0 ? (
        <p className="meta">{none}</p>
      ) : (
        <>
          {items.length > 8 && (
            <label>
              {searchLabel}
              <input type="search" value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
            </label>
          )}
          <div className="entry-choices">
            {shown.map((item) => (
              <label key={item.id} className="choice">
                <input type="checkbox" checked={chosenSet.has(item.id)} onChange={(e) => toggle(item.id, e.currentTarget.checked)} />
                {item.name} {item.note && <span className="meta">({item.note})</span>}
              </label>
            ))}
            {matches.length > shown.length && <p className="meta">{matches.length - shown.length} more. Type more of the name.</p>}
          </div>
        </>
      )}
    </fieldset>
  );
}
